import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Icon,
  Text,
} from '@chakra-ui/react';
import {
  Select as ChakraSelect,
  type GroupBase,
  type GroupProps,
  type SingleValue,
} from 'chakra-react-select';
import { useState } from 'react';

import { FeatureFlag, isSavedQueryVariableMetric } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';

import { useSavedQueries } from '../../api/use-saved-queries';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../PageConfigProvider/types/item';
import {
  groupedMetricOptions,
  type MetricOption,
  type MetricValues,
} from '../PageConfigProvider/types/metric';
import { BaseSelectStyles, menuPortalStyles } from './BaseSelectStyles';
import labelStyles from './section-label.module.css';
import { applySavedQuerySelectionToItem } from './utils/apply-saved-query-selection';
import { getAvailableMetrics } from './utils/metric-mapping';
import {
  filterSavedQueriesForChart,
  findSavedQueryByMetricValue,
  savedQueryMetricValue,
} from './utils/saved-query-for-chart';

type MetricSelectorProps = {
  item: PositionedItem;
  isDisabled?: boolean;
  isReport?: boolean;
};

export const MetricSelector = ({
  item,
  isDisabled = false,
  isReport = false,
}: MetricSelectorProps) => {
  const { config, parentId, dispatch } = useTabContentConfig();
  const reportParameters = config.reportParameters ?? [];
  const customReportsSnowflakeEnabled = useUnleashFlag(
    FeatureFlag.CustomReportsSnowflake
  );
  const { data: savedQueries = [] } = useSavedQueries({
    enabled: isReport && customReportsSnowflakeEnabled,
  });

  const primaryFilterId = config.primaryFilter?.id;
  const chartType = item?.plot?.chartType;

  const compatibleCustomQueries = (() => {
    if (
      !isReport ||
      !customReportsSnowflakeEnabled ||
      chartType === undefined ||
      chartType === null
    ) {
      return [];
    }
    return filterSavedQueriesForChart({
      queries: savedQueries,
      chartType,
      parentId,
    });
  })();

  const availableMetricOptions = (() => {
    const baseMetricGroups = getAvailableMetrics({
      primaryFilterId,
      chartType,
    });
    if (compatibleCustomQueries.length > 0) {
      baseMetricGroups.push({
        label: 'Custom variables',
        options: compatibleCustomQueries.map((q) => ({
          value: savedQueryMetricValue(q) as MetricValues,
          label: q.name,
        })),
      });
    }
    return baseMetricGroups;
  })();

  const id = item?.id;

  const { defaultMetricOptions } = item?.plot ?? { defaultMetricOptions: [] };
  const dataSource = item?.plot?.dataSource;
  const savedQueryId = item?.plot?.snowflakeConfig?.savedQueryId;

  const resolvedPlotMetric: MetricValues | undefined = (() => {
    if (
      defaultMetricOptions?.length === 1 &&
      defaultMetricOptions?.[0] === 'share_of_headcount'
    ) {
      return 'headcount';
    }
    if (
      dataSource === 'snowflake' &&
      savedQueryId !== undefined &&
      savedQueryId !== ''
    ) {
      const fromDto = savedQueries.find((q) => q.id === savedQueryId);
      if (fromDto) {
        return savedQueryMetricValue(fromDto) as MetricValues;
      }
      return `sq:${savedQueryId}` as MetricValues;
    }
    return defaultMetricOptions?.[0];
  })();

  const flatAvailableOptions = availableMetricOptions.flatMap(
    (group) => group.options
  );

  const matchedStandardOption = (() => {
    if (resolvedPlotMetric === undefined) return undefined;
    return flatAvailableOptions.find((opt) => opt.value === resolvedPlotMetric);
  })();

  const metricSelectOptions = (() => {
    if (
      primaryFilterId === undefined ||
      matchedStandardOption ||
      resolvedPlotMetric === undefined ||
      resolvedPlotMetric === null
    ) {
      return availableMetricOptions;
    }
    const orphanLabel =
      groupedMetricOptions
        .flatMap((group) => group.options)
        .find((option) => option.value === resolvedPlotMetric)?.label ??
      String(resolvedPlotMetric);
    return [
      {
        label: 'Current selection',
        options: [{ value: resolvedPlotMetric, label: orphanLabel }],
      },
      ...availableMetricOptions,
    ];
  })();

  const selectedMetric: MetricOption | null = (() => {
    if (
      defaultMetricOptions?.length === 1 &&
      defaultMetricOptions?.[0] === 'share_of_headcount'
    ) {
      return {
        value: 'headcount',
        label: 'Headcount',
      };
    }

    if (resolvedPlotMetric === undefined || resolvedPlotMetric === null) {
      return metricSelectOptions[0]?.options[0] ?? null;
    }

    const allForSelect = metricSelectOptions.flatMap((group) => group.options);
    const selected = allForSelect.find(
      (opt) => opt.value === resolvedPlotMetric
    );

    return selected ?? metricSelectOptions[0]?.options[0] ?? null;
  })();

  const showOrphanMetricWarning =
    primaryFilterId !== undefined &&
    resolvedPlotMetric !== undefined &&
    resolvedPlotMetric !== null &&
    matchedStandardOption === undefined;

  const handleChangeMetric = (metric: SingleValue<MetricOption>) => {
    if (!id) return;
    const next = metric?.value;
    if (next === undefined) {
      dispatch({
        type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
        payload: { id, defaultMetricOptions: [] },
      });
      return;
    }

    if (isSavedQueryVariableMetric(next)) {
      const query = findSavedQueryByMetricValue(savedQueries, next);
      if (!query) return;
      dispatch({
        type: 'UPDATE_ITEM_DATASOURCE',
        payload: { id, dataSource: 'snowflake' },
      });
      applySavedQuerySelectionToItem({
        itemId: id,
        query,
        reportParameters,
        dispatch,
      });
      dispatch({
        type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
        payload: { id, defaultMetricOptions: [next] },
      });
      return;
    }

    dispatch({
      type: 'UPDATE_ITEM_DATASOURCE',
      payload: { id, dataSource: 'mw-gql' },
    });
    dispatch({
      type: 'UPDATE_ITEM_DEFAULT_METRIC_OPTIONS',
      payload: { id, defaultMetricOptions: [next] },
    });
  };

  const CollapsibleGroup = (
    props: GroupProps<MetricOption, false, GroupBase<MetricOption>>
  ) => {
    const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

    const { data, children, innerProps } = props;

    const label = String(data.label);

    const isOpen = openGroups[label] ?? true;

    const toggle = () =>
      setOpenGroups((prev) => ({ ...prev, [label]: !(prev[label] ?? true) }));

    return (
      <Box {...innerProps}>
        <Flex px={2} py={1.5} cursor="pointer" align="center" onClick={toggle}>
          <Text flex="1" fontSize="sm" fontWeight="600">
            {label}
          </Text>
          <Icon
            as={ChevronDownIcon}
            transition="transform 0.2s"
            transform={isOpen ? 'rotate(180deg)' : undefined}
          />
        </Flex>

        {isOpen && <Box pl={2}>{children}</Box>}
      </Box>
    );
  };

  if (
    (!metricSelectOptions || metricSelectOptions.length === 0) &&
    !showOrphanMetricWarning
  ) {
    return null;
  }

  return (
    <Box marginBottom="1.5rem">
      <Flex alignItems="center" justifyContent="space-between" mb="0.5rem">
        <Text
          className={labelStyles.sectionLabel}
          fontSize="sm"
          fontWeight={600}
          opacity={isDisabled ? 0.6 : 1}
        >
          Variable
        </Text>
      </Flex>
      {showOrphanMetricWarning && (
        <Alert
          status="warning"
          variant="left-accent"
          fontSize="sm"
          borderRadius="md"
          mb={3}
          data-testid="metric-selector-unsupported-warning"
        >
          <AlertIcon />
          <AlertDescription>
            This chart&apos;s saved variable isn&apos;t available for the
            current chart type and filters. Choose a variable below or change
            the chart type.
          </AlertDescription>
        </Alert>
      )}
      <Flex>
        <ChakraSelect<MetricOption, false, GroupBase<MetricOption>>
          chakraStyles={BaseSelectStyles}
          menuPortalTarget={document.body}
          styles={menuPortalStyles}
          options={metricSelectOptions}
          value={selectedMetric}
          onChange={handleChangeMetric}
          isSearchable
          isDisabled={isDisabled}
          components={{
            Group: CollapsibleGroup,
          }}
        />
      </Flex>
    </Box>
  );
};
